import { BaseQueryFn } from '@reduxjs/toolkit/query'
import axios, { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios'
import { globalToaster } from 'components/elements/global'
import { API_BASE_URL } from 'config'
import { http } from 'lib/http'
import { RootState } from 'store'
import { ApiResponce } from 'types'

const getTokenFromUrl = (): string => {
 const urlParams = new URLSearchParams(window.location.search)
 return urlParams.get('token') ?? ''
}

type AxioQueryArg = {
 url: string
 method?: AxiosRequestConfig['method']
 body?: AxiosRequestConfig['data']
 params?: AxiosRequestConfig['params']
 // Per-request base URL override (e.g. main website API)
 baseUrl?: string
 // Caller handles errors itself (no global error toast) and accepts plain
 // JSON as well as the { status, data } wrapper
 raw?: boolean
}
const axiosBaseQuery =
 (
  { baseUrl }: { baseUrl?: string } = { baseUrl: `${API_BASE_URL}/` },
 ): BaseQueryFn<AxioQueryArg | string, unknown, unknown> =>
 async (arg, { getState, signal }) => {
  const source = axios.CancelToken.source()
  signal.addEventListener('abort', () => {
   source.cancel()
  })
  try {
   const { token: authToken } = (getState() as RootState).auth

   const token = authToken || getTokenFromUrl()

   const { data }: AxiosResponse<ApiResponce> = await http({
    url: `${(typeof arg !== 'string' && arg.baseUrl) || baseUrl}${
     typeof arg === 'string' ? arg : arg.url
    }`,
    method: typeof arg === 'string' ? 'get' : arg.method,
    data: typeof arg === 'string' ? '' : arg.body,
    params: typeof arg === 'string' ? '' : arg.params,
    cancelToken: source.token,
    headers: {
     Authorization: `Bearer ${token}`,
     Accept: 'application/json',
    },
   })

   // raw: the caller shows its own errors, so no global toast; a wrapped
   // { status: 'error' } reply is still returned as an error
   if (typeof arg !== 'string' && arg.raw) {
    return data?.status === 'error' || data?.status === false
     ? { error: { status: 400, data } }
     : { data }
   }
   if (data?.status === 'success' || data.status === true || data.success) {
    return { data }
   }
   globalToaster({ type: 'error', content: data.message })
   return { error: { status: 400, data } }
  } catch (axiosError) {
   const err = axiosError as AxiosError
   return {
    error: { status: err.response?.status, data: err.response?.data },
   }
  }
 }

export default axiosBaseQuery
